import { useState, useEffect } from "react";
import THEME from "../../../theme";
import { fs } from "../utils/styles";
import { finanzenApi } from "../services/finanzenApi";

const MONATE = ["", "Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const TYP_FILTER = [["", "Alle Typen"], ["bwa_susa", "BWA/SuSa"], ["opos", "OPOS-Liste"], ["kontenblatt", "Kontenblatt"], ["klaerungskonto", "Klärungskonto"], ["ustva", "USt-Voranmeldung"]];
const ECHTHEIT_TEXT = {
  smime_ok: "✓ S/MIME-Signatur",
  spf_ok: "✓ Absender (SPF)",
  dkim_ok: "✓ Absender (DKIM)",
};

function groesse(bytes) {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function zeitraum(d) {
  if (!d.zeitraum_jahr) return "–";
  return d.zeitraum_monat ? `${MONATE[d.zeitraum_monat]} ${d.zeitraum_jahr}` : `${d.zeitraum_jahr}`;
}

// Nur-Lese-Ansicht auf das revisionssichere Dokumentenarchiv der Steuerkanzlei (Phase 1 des Buchhaltungs-Ausbaus).
// Dokumente koennen hier weder geaendert noch geloescht werden; jeder Abruf wird protokolliert.
export default function OriginaldokumenteListe({ benutzerId }) {
  const [offen, setOffen] = useState(false);
  const [dokumente, setDokumente] = useState(null);
  const [typ, setTyp] = useState("");
  const [fehler, setFehler] = useState("");
  const [meldung, setMeldung] = useState("");
  const [pruefung, setPruefung] = useState(false);

  useEffect(() => {
    if (!offen) return;
    setFehler("");
    finanzenApi.dokumenteListe(benutzerId, typ).then(setDokumente).catch(e => { setFehler(e.message); setDokumente([]); });
  }, [offen, typ, benutzerId]);

  const oeffnen = async (d, art) => {
    setFehler("");
    const fenster = art === "datei" ? window.open("", "_blank") : null; // Fenster synchron oeffnen, sonst blockt der Browser
    try {
      const blob = await finanzenApi.dokumentBlob(d.id, art);
      const url = URL.createObjectURL(blob);
      if (fenster) {
        fenster.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = d.dateiname.replace(/\.pdf$/i, "") + ".eml";
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (fenster) fenster.close();
      setFehler(e.message);
    }
  };

  const archivPruefen = async () => {
    setPruefung(true);
    setMeldung("");
    try {
      const r = await finanzenApi.dokumenteIntegritaet(benutzerId);
      setMeldung(r.ok ? `✓ Alle ${r.geprueft} Dokumente sind unverändert (Prüfsummen stimmen).` : `⚠ ${r.probleme.length} Auffälligkeit(en): ${r.probleme.join("; ")}`);
    } catch (e) {
      setMeldung("Prüfung fehlgeschlagen: " + e.message);
    }
    setPruefung(false);
  };

  return (
    <div style={fs.karte}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ color: THEME.colors.text, margin: 0 }}>📂 Originaldokumente der Steuerkanzlei</h3>
          <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: "4px 0 0" }}>
            Unveränderlich archiviert (Prüfsumme + Original-Mail). Aufbewahrung vorläufig 10 Jahre, keine automatische Löschung. Jeder Abruf wird protokolliert.
          </p>
        </div>
        <button style={{ ...fs.btnGray, width: "auto" }} onClick={() => setOffen(!offen)}>{offen ? "Zuklappen" : "Anzeigen"}</button>
      </div>

      {offen && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
            <select style={{ ...fs.input, width: "auto" }} value={typ} onChange={e => setTyp(e.target.value)}>
              {TYP_FILTER.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button style={{ ...fs.btnGray, width: "auto" }} onClick={archivPruefen} disabled={pruefung}>{pruefung ? "Prüfe…" : "🔍 Archiv auf Unversehrtheit prüfen"}</button>
            {dokumente && <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{dokumente.length} Dokument(e)</span>}
          </div>
          {meldung && <p style={{ color: meldung.startsWith("✓") ? THEME.colors.success : THEME.colors.warning, fontSize: THEME.fontSize.base }}>{meldung}</p>}
          {fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.base }}>{fehler}</p>}
          <div style={fs.tabelle}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={fs.th}>Typ</th><th style={fs.th}>Zeitraum</th><th style={fs.th}>Datei</th><th style={fs.th}>Mail vom</th>
                  <th style={fs.th}>Echtheit</th><th style={fs.th}>Größe</th><th style={fs.th}>Prüfsumme</th><th style={fs.th}>Aufbewahren bis</th><th style={fs.th}></th>
                </tr>
              </thead>
              <tbody>
                {(dokumente || []).map(d => (
                  <tr key={d.id}>
                    <td style={fs.td}>{d.doktyp_name}</td>
                    <td style={fs.td}>{zeitraum(d)}</td>
                    <td style={{ ...fs.td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.dateiname}>{d.dateiname}</td>
                    <td style={fs.td}>{d.mail_datum ? new Date(d.mail_datum).toLocaleDateString("de-DE") : "–"}</td>
                    <td style={{ ...fs.td, color: THEME.colors.success }}>{ECHTHEIT_TEXT[d.echtheit_status] || d.echtheit_status}</td>
                    <td style={fs.td}>{groesse(d.groesse_bytes)}</td>
                    <td style={{ ...fs.td, fontFamily: "ui-monospace, Consolas, monospace", fontSize: THEME.fontSize.sm }}>{d.sha256_kurz}</td>
                    <td style={fs.td}>{new Date(d.aufbewahren_bis).toLocaleDateString("de-DE")}</td>
                    <td style={{ ...fs.td, whiteSpace: "nowrap" }}>
                      <button style={{ ...fs.btnGray, width: "auto", marginRight: 6, padding: "6px 12px" }} onClick={() => oeffnen(d, "datei")}>Ansehen</button>
                      {d.hat_original_mail && <button style={{ ...fs.btnGray, width: "auto", padding: "6px 12px" }} onClick={() => oeffnen(d, "eml")}>Original-Mail</button>}
                    </td>
                  </tr>
                ))}
                {dokumente && dokumente.length === 0 && !fehler && <tr><td style={fs.td} colSpan={9}>Keine Dokumente archiviert.</td></tr>}
                {!dokumente && <tr><td style={fs.td} colSpan={9}>Lädt…</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
